// "Followed N Skills" under an answer: the company Skills Poneva read before
// writing it, and why — picked for the chat, pinned by the agent, found with
// find_right_skill for a need, or read otherwise. The turn writes it as a
// durable `data-ponevaSkills` part (src/company-workspace/followed-skills.mjs),
// each part holding everything read so far. A row opens the Skill at the exact
// version that was followed. It says what Poneva was given before the text,
// not that every step was followed.
import { getExternalStoreMessages, useAuiState } from '@assistant-ui/react';
import { useMemo } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import skillsCopy from '@/i18n/ns/skills';
import { useOptionalWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import './followed-skills.css';

export const FOLLOWED_SKILLS_PART = 'data-ponevaSkills';
const FOLLOWED_SKILLS_NAME = 'ponevaSkills';
const MAX_ITEMS = 50;
const HOWS = ['picked', 'agent', 'found', 'read'] as const;

export type FollowedHow = (typeof HOWS)[number];
export interface FollowedSkill {
  id: string;
  revision: number;
  name: string;
  how: FollowedHow;
  need?: string;
}

function parseItem(value: unknown): FollowedSkill | null {
  const item = value as Partial<FollowedSkill> | null;
  if (
    !item ||
    typeof item.id !== 'string' ||
    !Number.isInteger(item.revision) ||
    typeof item.name !== 'string' ||
    !HOWS.includes(item.how as FollowedHow)
  )
    return null;
  return {
    id: item.id,
    revision: item.revision as number,
    name: item.name,
    how: item.how as FollowedHow,
    ...(typeof item.need === 'string' && item.need ? { need: item.need } : {}),
  };
}

/** The Skills an answer followed: its last record part (each one is cumulative). */
export function followedSkills(parts: readonly unknown[]): FollowedSkill[] {
  for (const value of [...parts].reverse()) {
    const part = value as { type?: string; name?: string; data?: { items?: unknown } } | null;
    const record =
      part?.type === FOLLOWED_SKILLS_PART ||
      (part?.type === 'data' && part.name === FOLLOWED_SKILLS_NAME);
    if (!record) continue;
    const items = Array.isArray(part?.data?.items) ? part.data.items : [];
    return items
      .slice(0, MAX_ITEMS)
      .map(parseItem)
      .filter((item): item is FollowedSkill => item !== null);
  }
  return [];
}

type SkillsCopy = ReturnType<typeof useNamespaceCopy<keyof (typeof skillsCopy)['en']>>;

/** Why a Skill was followed, in the reader's words. */
export function followedReason(copy: SkillsCopy, how: FollowedHow, need?: string) {
  if (how === 'found') return need ? formatCopy(copy.howFound, { need }) : copy.howFoundPlain;
  if (how === 'picked') return copy.howPicked;
  if (how === 'agent') return copy.howAgent;
  return copy.howRead;
}

export function FollowedSkills() {
  const key = useAuiState((state) => {
    const originals = getExternalStoreMessages(state.message) as { parts?: unknown[] }[];
    const parts = originals.length
      ? originals.flatMap((message) => message.parts ?? [])
      : (state.message.parts as readonly unknown[]);
    return JSON.stringify(followedSkills(parts));
  });
  const items = useMemo(() => JSON.parse(key) as FollowedSkill[], [key]);
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(skillsCopy);
  if (!items.length) return null;
  const title = formatCopy(items.length === 1 ? copy.followedOne : copy.followedMany, {
    count: items.length,
  });
  return (
    <section className="ws-followed" data-slot="followed-skills" aria-label={title}>
      <header className="ws-followed-head">
        <WorkspaceIcon name="skills" size={15} />
        <strong>{title}</strong>
        <span>{copy.followedSubtitle}</span>
      </header>
      {items.map((item) => {
        const body = (
          <>
            <WorkspaceIcon name="skills" size={15} />
            <span className="ws-followed-text">
              <span className="ws-followed-name">
                <b>{item.name}</b>
                <span className="ws-followed-version">
                  {formatCopy(copy.cardVersion, { revision: item.revision })}
                </span>
              </span>
              <small>{followedReason(copy, item.how, item.need)}</small>
            </span>
          </>
        );
        return ws ? (
          <button
            type="button"
            className="ws-followed-row"
            key={item.id}
            data-canvas-trigger="true"
            aria-label={formatCopy(copy.openFollowed, { name: item.name, revision: item.revision })}
            onClick={() =>
              ws.openCanvas({
                kind: 'skill',
                id: item.id,
                revision: String(item.revision),
                followed: item.how,
                ...(item.need ? { need: item.need } : {}),
              })
            }
          >
            {body}
            <WorkspaceIcon name="chevron" size={14} className="ws-followed-open" />
          </button>
        ) : (
          <div className="ws-followed-row" key={item.id}>
            {body}
          </div>
        );
      })}
    </section>
  );
}
